import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, User, Loader2, Building2, CalendarClock, Hourglass } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { IhsgChart } from "@/components/IhsgChart";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [terbaru, setTerbaru] = useState([]);

  useEffect(() => {
    api
      .get("/ipo/publik/terbaru")
      .then((r) => setTerbaru(r.data))
      .catch(() => setTerbaru([]));
  }, []);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(username.trim(), password);
    setBusy(false);
    if (!res.ok) return setError(res.error);
    navigate(res.user.role === "anggota" ? "/dashboard" : "/ipo", { replace: true });
  };

  return (
    <div className="relative z-10 grid min-h-screen lg:grid-cols-[1.05fr_1fr]">
      <div className="relative hidden overflow-hidden border-r border-[#1e2536] lg:block">
        <img
          src="https://images.pexels.com/photos/6203470/pexels-photo-6203470.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Grafik pasar saham"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-[#080b11]/70" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets-jai6qajn.emergentagent.net/job_stock-invest-hub-3/artifacts/mw294pcw_harimau-removebg-preview.png"
              alt="Cuan IPO"
              className="h-12 w-12 object-contain"
            />
            <p className="text-lg font-bold tracking-tight text-white">Cuan IPO</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-[#00d09c]">Bursa Efek Indonesia</p>
            <h1 className="mt-5 max-w-lg text-4xl font-bold leading-[1.1] tracking-tight text-white lg:text-5xl">
              Kelola portofolio IPO & saldo anggota di satu terminal.
            </h1>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-[#8f9bb3]">
              Data penawaran umum, mutasi saldo, dan profil anggota terhubung rapi dalam satu
              sistem yang aman.
            </p>
          </div>
          <div className="flex gap-10">
            {[
              ["Data IPO", "Realtime"],
              ["Mutasi Saldo", "Terlacak"],
              ["Hak Akses", "Berlapis"],
            ].map(([k, v]) => (
              <div key={k}>
                <p className="font-mono-num text-sm text-[#00d09c]">{v}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.16em] text-[#4d586f]">{k}</p>
              </div>
            ))}
          </div>

          {terbaru.length > 0 && (
            <div data-testid="login-ipo-terbaru" className="mt-10">
              <p className="text-xs uppercase tracking-[0.22em] text-[#00d09c]">IPO Terbaru</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                {terbaru.map((s, i) => (
                  <motion.div
                    key={s.id}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.15 + i * 0.12 }}
                    data-testid={`login-ipo-card-${s.kode_saham}`}
                    className="rounded-lg border border-white/10 bg-black/50 p-4 backdrop-blur-xl transition-transform duration-200 hover:-translate-y-1"
                  >
                    <div className="flex items-center gap-2.5">
                      {s.logo_url ? (
                        <img
                          src={s.logo_url}
                          alt={s.nama_saham}
                          className="h-8 w-8 shrink-0 rounded object-contain"
                          onError={(e) => (e.currentTarget.style.display = "none")}
                        />
                      ) : (
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-white/10">
                          <Building2 size={14} className="text-[#4d586f]" />
                        </div>
                      )}
                      <span className="font-mono-num text-sm font-semibold text-[#00d09c]">
                        {s.kode_saham}
                      </span>
                    </div>
                    <p className="mt-2.5 line-clamp-2 text-sm leading-snug text-white">
                      {s.nama_saham}
                    </p>
                    <p className="mt-1 text-[11px] uppercase tracking-[0.14em] text-[#4d586f]">
                      {s.sektor || "-"}
                    </p>
                    <div className="mt-3 space-y-1.5 border-t border-white/10 pt-3">
                      {typeof s.sisa_hari_listing === "number" && (
                        <p
                          data-testid={`login-countdown-${s.kode_saham}`}
                          className={`font-mono-num flex items-center gap-1.5 text-xs font-semibold ${
                            s.sisa_hari_listing < 0 ? "text-[#8f9bb3]" : "text-[#00d09c]"
                          }`}
                        >
                          <Hourglass size={12} />
                          {s.sisa_hari_listing > 0
                            ? `${s.sisa_hari_listing} hari lagi listing`
                            : s.sisa_hari_listing === 0
                            ? "Listing hari ini"
                            : "Sudah listing"}
                        </p>
                      )}
                      <p className="font-mono-num flex items-center gap-1.5 text-xs text-[#8f9bb3]">
                        <CalendarClock size={12} /> {s.tanggal_listing || "-"}
                      </p>
                      <p className="font-mono-num text-xs text-white">
                        {s.harga_final ? `Final ${s.harga_final}` : `Range ${s.range_harga || "-"}`}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-14">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="mb-9 lg:hidden">
            <img
              src="https://customer-assets-jai6qajn.emergentagent.net/job_stock-invest-hub-3/artifacts/mw294pcw_harimau-removebg-preview.png"
              alt="Cuan IPO"
              className="h-14 w-14 object-contain"
            />
          </div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#00d09c]">Akses Terbatas</p>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-white">Masuk Akun</h2>
          <p className="mt-2 text-sm text-[#8f9bb3]">
            Gunakan kredensial yang diberikan oleh Admin.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label className="text-xs uppercase tracking-[0.14em] text-[#8f9bb3]">Username</Label>
              <div className="relative mt-2">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4d586f]" />
                <Input
                  data-testid="login-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="username"
                  autoComplete="username"
                  className="h-11 border-[#1e2536] bg-[#10141d] pl-9 text-white placeholder:text-[#3a4457] focus-visible:ring-[#00d09c]"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.14em] text-[#8f9bb3]">Password</Label>
              <div className="relative mt-2">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4d586f]" />
                <Input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="h-11 border-[#1e2536] bg-[#10141d] pl-9 text-white placeholder:text-[#3a4457] focus-visible:ring-[#00d09c]"
                />
              </div>
            </div>
            {error && (
              <p data-testid="login-error" className="rounded-md border border-[#ff3b30]/30 bg-[#ff3b30]/10 px-3 py-2 text-sm text-[#ff3b30]">
                {error}
              </p>
            )}
            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={busy}
              className="h-11 w-full rounded-full bg-[#00d09c] font-semibold text-[#080b11] transition-colors duration-200 hover:bg-[#00e5ff]"
            >
              {busy ? <Loader2 size={17} className="animate-spin" /> : "Masuk Terminal"}
            </Button>
          </form>

          <div className="mt-8">
            <IhsgChart />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
